import { Alert, Box, Grid } from '@mui/material'
import { Form, Formik } from 'formik'
import { DateTime } from 'luxon'
import React from 'react'
import { PatientHistoryType } from 'src/graphql.generated'
import useAddPatientHistoryEntry from 'src/participantHistory/useAddPatientHistoryEntry'
import { toFormikValidationSchema } from 'zod-formik-adapter'
import { Loader } from 'src/designSystem'
import { NoteDraftLoadError } from 'src/noteDraft/NoteDraftAlerts'
import TreatmentPlanComprehensiveFields from './TreatmentPlanComprehensiveFields'
import SubmitButton from '../SubmitButton'
import { clearFormFromLocalStorage } from '../persistFormHelpers'
import FormPersistor from '../FormPersistor'
import {
  TREATMENT_PLAN_COMPREHENSIVE_FORM_SCHEMA,
  TreatmentPlanComprehensiveFormType,
} from './TreatmentPlanComprehensiveFormSchema'
import { useLocalStorageWithPrefillValues } from '../useLocalStorageWithPrefillValues'

const getDefaultValues = () => ({
  date: DateTime.now().toFormat('yyyy-MM-dd'),
  FIRST_NAME: '',
  LAST_NAME: '',
  MEMBER_ID: '',
  PROVIDER_NAME: '',
  LICENSE_TYPE: '',
  PRESENTING_PROBLEM: '',
  LONG_TERM_GOAL: '',
  BILLING_CODES: [],
  SHORT_TERM_GOAL: {
    SPECIFIC: '',
    MEASURABLE: '',
    ACHIEVABLE: 'Yes',
    RELEVANT: '',
    TIME_BOUND: '12 weeks',
    GOAL_STATEMENT: '',
  },
  INTERVENTIONS_AND_PARTICIPANT_AGREEMENT: {
    PHQ9_GAD7: true,
    BIOFEEDBACK: true,
    IN_APP_CHAT: true,
    CBT_MINDFULLNESS_ACT: true,
    PSYCHOEDUCATION: true,
    TELEHEALTH_VIDEO: true,
    PEER_SUPPORT: true,
    STRENGTHS: '',
    OTHER: '',
    PARTICIPANT_AGREEMENT: false,
  },
})

type Props = {
  formType: PatientHistoryType.TreatmentPlanComprehensive | PatientHistoryType.TreatmentPlanComprehensiveUpdate
  uid: string
  afterSubmittedCallback?: () => void
}

const TreatmentPlanForm = ({ formType, uid, afterSubmittedCallback }: Props) => {
  const addPatientHistoryEntry = useAddPatientHistoryEntry()

  const { values: initialValues, status } = useLocalStorageWithPrefillValues(uid, formType, getDefaultValues)

  if (status === 'draftError') {
    return <NoteDraftLoadError />
  }
  if (status === 'loading') {
    return <Loader size="large" />
  }

  return (
    <Grid item>
      <Formik<TreatmentPlanComprehensiveFormType>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={toFormikValidationSchema(TREATMENT_PLAN_COMPREHENSIVE_FORM_SCHEMA)}
        onSubmit={async (values, { resetForm }) => {
          await addPatientHistoryEntry({
            uid,
            date: values.date,
            type: formType,
            value: values,
          })
          resetForm({ values: getDefaultValues() })
          clearFormFromLocalStorage(uid, formType)
          afterSubmittedCallback?.()
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <FormPersistor uid={uid} formType={formType} />
            {status === 'error' && (
              <Box mt={3}>
                <Alert severity="error">Failed to load form prefill data.</Alert>
              </Box>
            )}

            <TreatmentPlanComprehensiveFields />
            <Box justifyContent="flex-end" display="flex" mt={4}>
              <SubmitButton isSubmitting={isSubmitting} />
            </Box>
          </Form>
        )}
      </Formik>
    </Grid>
  )
}

export default TreatmentPlanForm
